/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { Link } from '@client/components';
import type { Icon } from 'lucide-preact';

import { SidebarRailItem } from '@/client/components/shared/SidebarRail';

/*----------------------------------
- TYPES
----------------------------------*/

export interface SidebarNavigationLinkProps {
    label: string;
    href: string;
    icon: Icon;
    dataTestId: string;
    active?: boolean;
    disabled?: boolean;
    collapsed?: boolean;
    isMobile?: boolean;
    onNavigate?: () => void;
    iconSize?: number;
    variant?: 'default' | 'child';
}

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function SidebarNavigationLink({
    label,
    href,
    icon: IconComponent,
    dataTestId,
    active,
    disabled = false,
    collapsed = false,
    isMobile = false,
    onNavigate,
    iconSize,
    variant = 'default',
}: SidebarNavigationLinkProps) {
    if (collapsed && !isMobile) {
        return (
            <SidebarRailItem
                label={label}
                icon={<IconComponent size={18} strokeWidth={1.75} />}
                href={href}
                active={active}
                disabled={disabled}
                dataTestId={dataTestId}
            />
        );
    }

    const resolvedIconSize = iconSize ?? (variant === 'child' ? 16 : 20);
    const sharedClasses = `flex items-center gap-3 font-medium transition-all ${
        variant === 'child' ? 'rounded-lg px-3 py-2' : 'rounded-xl px-3 py-2.5'
    }`;
    const enabledClasses = active
        ? 'bg-primary-50 text-primary-700 shadow-sm ring-1 ring-primary-100'
        : 'text-slate-700 hover:bg-primary-50/70 hover:text-primary-700 hover:ring-1 hover:ring-primary-100/70';
    const disabledClasses = 'cursor-not-allowed text-slate-400 opacity-50';

    if (disabled) {
        return (
            <div
                className={`${sharedClasses} ${disabledClasses}`}
                aria-disabled="true"
                data-testid={dataTestId}
                data-active={active === undefined ? undefined : active ? 'true' : 'false'}
            >
                <IconComponent size={resolvedIconSize} className="text-slate-400" />
                {(!collapsed || isMobile) && <span className="text-sm">{label}</span>}
            </div>
        );
    }

    return (
        <Link
            to={href}
            className={`${sharedClasses} ${enabledClasses}`}
            onClick={isMobile ? onNavigate : undefined}
            data-testid={dataTestId}
            aria-current={active ? 'page' : undefined}
            aria-label={collapsed && !isMobile ? label : undefined}
            data-active={active === undefined ? undefined : active ? 'true' : 'false'}
            title={collapsed && !isMobile ? label : undefined}
        >
            <IconComponent size={resolvedIconSize} />
            {(!collapsed || isMobile) && <span className="text-sm">{label}</span>}
        </Link>
    );
}
